// 14 Oct (the Tbilisi streams): the open battle a player can go to next, from GET /battles/open. On the front page it
// is "live now or starting soon" for a student who typed the address from a Live; on the result page it is the next
// battle of the evening, one tap from the join card (which already knows the remembered name and email). Battles run
// back to back, so this is how a room carries over from one battle to the next without a new code.
import { Link } from 'react-router-dom';
import type { OpenBattleRow } from '@shared/types';
import { fetchOpenBattles } from '@/creator/api';
import { buttonClass } from '@/components/Button';
import { fmt, usePolling } from '@/lib/live';
import { clockAt } from '@/lib/studio';
import { clockLabel, startsAtLabel, useSecondsUntil, yourTime } from './time';
import { HostedBy } from '@/components/HostedBy';

export const NEXT_REFRESH_MS = 10_000;
const UNDER_WAY = new Set(['question', 'reveal', 'leaderboard']);
/** Under this, a waiting battle's card counts down instead of giving the time of day. */
export const COUNTDOWN_FROM_S = 3600;

export const NEXT = {
  live: 'Live now',
  soon: 'Starting soon',
  next: 'Next battle',
  join: 'Join',
  joinNext: 'Join the next battle',
  playing: (n: number) => `${fmt(n)} playing`,
  waiting: (n: number) => `${fmt(n)} ${n === 1 ? 'medic' : 'medics'} in the lobby`,
  startsAt: (at: number) => `${startsAtLabel(at)} ${clockAt(at)} ${yourTime(at)}`,
  startsIn: (secs: number) => `Starts in ${clockLabel(secs)}`,
};

/** The battles worth showing, best first: under way, then waiting in the lobby by start time; never `except`, never over. */
export function pickNext(rows: readonly OpenBattleRow[], except?: string, now: number = Date.now()): OpenBattleRow[] {
  const skip = except?.toUpperCase();
  const live = rows.filter((r) => r.code !== skip && UNDER_WAY.has(r.status));
  const lobby = rows.filter((r) => r.code !== skip && r.status === 'lobby')
    .sort((a, b) => (a.scheduledStartAt ?? now + 86_400_000) - (b.scheduledStartAt ?? now + 86_400_000));
  return [...live, ...lobby];
}

/** One card: the battle, its state, and the way in. `source` tags the join (?ref=) for the share and carry-over counts. */
export function NextBattleCard({ row, heading, cta, source, showCount = true }: { row: OpenBattleRow; heading: string; cta: string; source?: string; showCount?: boolean }) {
  const live = UNDER_WAY.has(row.status);
  // 14 Oct 2026: under an hour away, the card counts down live ("Starts in 3:12"), like the lobby; further off, the time of day.
  const secs = useSecondsUntil(!live && row.status === 'lobby' ? row.scheduledStartAt : undefined);
  const soon = secs !== undefined && secs > 0 && secs < COUNTDOWN_FROM_S;
  const line = showCount ? countLine(row, live, soon ? secs : undefined) : timeLine(row, live, soon ? secs : undefined);
  const to = `/j/${row.code}${source ? `?ref=${encodeURIComponent(source)}` : ''}`;
  return (
    <section aria-label={heading} data-part="next-battle" className="rd-card flex min-w-0 flex-col gap-3 p-5">
      <p className="text-sm font-semibold text-ink-2">{heading}</p>
      <div className="min-w-0">
        <p className="break-words font-display text-2xl font-bold leading-tight text-ink">{row.title}</p>
        {row.hostName && <HostedBy host={row.hostName} className="text-ink-2" />}
        {line && <p className="mt-1 text-sm font-semibold text-brand-strong">{line}</p>}
      </div>
      <Link to={to} className={buttonClass({ className: 'w-full' })} aria-label={`${cta}: ${row.title}`}>{cta}</Link>
    </section>
  );
}

/** The card's line with the room's count, as the join page and the result page show it. */
function countLine(row: OpenBattleRow, live: boolean, secs: number | undefined): string {
  if (live) return `${NEXT.live} · ${NEXT.playing(row.playerCount)}`;
  if (secs !== undefined) return `${NEXT.startsIn(secs)} · ${NEXT.waiting(row.playerCount)}`;
  if (row.scheduledStartAt && row.scheduledStartAt > Date.now()) return `${NEXT.startsAt(row.scheduledStartAt)} · ${NEXT.waiting(row.playerCount)}`;
  return `${NEXT.soon} · ${NEXT.waiting(row.playerCount)}`;
}

/** The line without a count (the front page, 30 Oct 2026): only the start time, when there is one; the heading already says live or soon. */
function timeLine(row: OpenBattleRow, live: boolean, secs: number | undefined): string | undefined {
  if (live) return undefined;
  if (secs !== undefined) return NEXT.startsIn(secs);
  if (row.scheduledStartAt && row.scheduledStartAt > Date.now()) return NEXT.startsAt(row.scheduledStartAt);
  return undefined;
}

/** Polls the open battles while shown; renders the best one (or nothing). `showCount` false leaves the room's count off the card. */
export function NextBattle({ except, heading, cta, source, enabled = true, showCount = true }: { except?: string; heading: (row: OpenBattleRow) => string; cta: string; source?: string; enabled?: boolean; showCount?: boolean }) {
  const polled = usePolling(fetchOpenBattles, NEXT_REFRESH_MS, [], { enabled });
  const row = polled.data ? pickNext(polled.data, except)[0] : undefined;
  return row ? <NextBattleCard row={row} heading={heading(row)} cta={cta} source={source} showCount={showCount} /> : null;
}
